// src/components/Dashboard/InsightsCard.tsx
import React, { useMemo } from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import SectionHeader from '../SectionHeader';
import { useAccountBookStore } from '@/store/accountBookStore';
import { useEmployeeData } from '@/hooks';
import { useMinWageHourly, useWeeklyRestMinHours } from '../../hooks/useRates';
import { generateInsights, type InsightTone } from '../../utils/insights';
import { detectLaborRisks } from '../../utils/laborRisk';

const TONE_COLOR: Record<InsightTone, string> = {
  up: 'var(--c-green-700)',
  good: 'var(--c-green-700)',
  down: 'var(--c-orange-600)',
  warn: 'var(--c-red-600)',
  neutral: 'var(--fg-secondary)',
};

const InsightsCard: React.FC = () => {
  const dashboardData = useAccountBookStore((s) => s.dashboardData);
  const insights = useMemo(
    () =>
      generateInsights({
        kpiData: dashboardData.kpiData,
        previousYearKpiData: dashboardData.previousYearKpiData,
        monthlyData: dashboardData.monthlyData,
        categoryExpenses: dashboardData.categoryExpenses,
      }),
    [dashboardData],
  );

  // 노무 리스크를 인사이트 카드에 통합 — 재무 인사이트 아래에 주의/확인 칩으로 노출
  const { employees } = useEmployeeData();
  const minWageHourly = useMinWageHourly();
  const weeklyRestMinHours = useWeeklyRestMinHours();
  const laborRisks = useMemo(
    () => detectLaborRisks(employees, { minWageHourly, weeklyRestMinHours }),
    [employees, minWageHourly, weeklyRestMinHours],
  );

  if (insights.length === 0 && laborRisks.length === 0) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        background: 'var(--bg-base)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--r-20)',
        p: { xs: 2, sm: 2.5, md: 3 },
      }}
    >
      <SectionHeader size="card" eyebrow="AI 분석" title="AI가 본 이번 기간" />
      <Stack spacing={1.5} sx={{ mt: 2 }}>
        {insights.map((ins, i) => (
          <Box key={`ins-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: 'var(--r-pill)',
                backgroundColor: TONE_COLOR[ins.tone],
                flexShrink: 0,
              }}
            />
            <Typography sx={{ fontSize: 'var(--size-md)', color: 'var(--fg-strong)' }}>
              {ins.text}
            </Typography>
          </Box>
        ))}
        {laborRisks.map((r, i) => (
          <Box key={`labor-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Chip
              label={r.severity === 'high' ? '주의' : '확인'}
              size="small"
              sx={{
                backgroundColor: r.severity === 'high' ? 'var(--c-red-600)' : 'var(--c-orange-600)',
                color: 'var(--fg-on-primary)',
                fontWeight: 600,
                flexShrink: 0,
              }}
            />
            <Typography sx={{ fontSize: 'var(--size-md)', color: 'var(--fg-strong)' }}>
              {r.message}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

export default InsightsCard;
